import { BarGraphData, LineGraphData } from 'pages/Analytics/types';

const NAME_COLUMN = 'Name';

type RankedExportPoint = {
  journey_id?: string;
  name?: string;
  value?: number;
  y?: number;
};

/** Make sure CSV column names don't clash (e.g. two columns both called "Time"). */
export function resolveLineGraphExportHeaders(
  xAxisLabel = 'Time',
  yAxisLabel = 'Value',
): { nameHeader: string; xHeader: string; yHeader: string } {
  const used = new Set<string>([NAME_COLUMN]);

  const takeUnique = (preferred: string, fallback: string): string => {
    const base = preferred.trim() || fallback;
    if (!used.has(base)) {
      used.add(base);
      return base;
    }

    const withFallback = `${base} (${fallback})`;
    if (!used.has(withFallback)) {
      used.add(withFallback);
      return withFallback;
    }

    let suffix = 2;
    let candidate = `${base} (${suffix})`;
    while (used.has(candidate)) {
      suffix += 1;
      candidate = `${base} (${suffix})`;
    }
    used.add(candidate);
    return candidate;
  };

  return {
    nameHeader: NAME_COLUMN,
    xHeader: takeUnique(xAxisLabel, 'Time'),
    yHeader: takeUnique(yAxisLabel, 'Value'),
  };
}

/** Turn line-chart points into CSV rows. Column titles come from the chart axes. */
export function buildLineGraphExportRows(
  lineData: LineGraphData,
  xAxisLabel = 'Time',
  yAxisLabel = 'Value',
): Record<string, string>[] {
  const { nameHeader, xHeader, yHeader } = resolveLineGraphExportHeaders(xAxisLabel, yAxisLabel);

  return lineData.map((dataPoint) => ({
    [nameHeader]: dataPoint.name ?? '',
    [xHeader]: String(dataPoint.x ?? ''),
    [yHeader]: String(dataPoint.y ?? ''),
  }));
}

/** Turn bar-chart points into CSV rows (Journey ID, Name, Value). */
export function buildBarGraphExportRows(barData: BarGraphData): Record<string, string>[] {
  return barData.map((dataPoint) => ({
    'Journey ID': dataPoint.journey_id ?? '',
    Name: dataPoint.name ?? '',
    Value: String(dataPoint.value ?? dataPoint.y ?? ''),
  }));
}

/** Turn table rows into CSV rows. Uses `value`, or `y` if `value` is missing. */
export function buildTableGraphExportRows(tableData: RankedExportPoint[]): Record<string, string>[] {
  return tableData.map((dataPoint) => ({
    'Journey ID': dataPoint.journey_id ?? '',
    Name: dataPoint.name ?? '',
    Value: String(dataPoint.value ?? dataPoint.y ?? ''),
  }));
}
